import { Fragment, useMemo, useState } from "react";
import styled from "styled-components";

import { Badge, Table } from "../../../common/components/core";
import { formatToDateWithTime } from "../../../utils/common";
import { InvoiceStatus } from "../../../types/invoice";
import { ClaimStatus, CommentRecord } from "../../../types/claim";
import { Comment } from "../../../types/common";
import { Icon } from "@blueprint/ui";

export enum PORejectType {
    CLAIM_REJECT = "CLAIM_REJECT",
    INVOICE_REJECT = "INVOICE_REJECT",
}

export type ClaimRejectProps = {
    rejectType: PORejectType.CLAIM_REJECT;
    claimStatus: ClaimStatus;
};

export type InvoiceRejectProps = {
    rejectType: PORejectType.INVOICE_REJECT;
    invoiceStatus: InvoiceStatus;
};

type PORejectedTableProps = {
    comment: CommentRecord | Comment;
    rejectType: PORejectType;
    status: ClaimStatus | InvoiceStatus;
};

const generateApproverStatusBadge = (status?: InvoiceStatus | ClaimStatus) => {
    return status &&
        [
            InvoiceStatus.PO_REJECTED,
            ClaimStatus.PO_REJECTED,
            InvoiceStatus.FINANCE_REJECTED,
        ].includes(status) ? (
        <Badge type="danger">Rejected</Badge>
    ) : (
        <Badge type="success">Approved</Badge>
    );
};


export const PORejectedTable = (props: PORejectedTableProps): JSX.Element => {
    const [isExpand, setExpand] = useState(false);
    const handleToggleArrow = () => {
        setExpand((prev) => !prev);
    };
    const { comment, rejectType, status } = props;
    const isCommentRecord = (c: Comment | CommentRecord): c is CommentRecord =>
    'timestamp' in c;
    const time = isCommentRecord(comment) ? comment.timestamp : comment.created;
    const ref = isCommentRecord(comment) ? comment.commentRef : comment.comment;
    const isEnableRejectArrow = useMemo(() => {
        if (rejectType === PORejectType.CLAIM_REJECT) {
            return status === ClaimStatus.PO_REJECTED;
        }
        return (
            status === InvoiceStatus.PO_REJECTED ||
            status === InvoiceStatus.FINANCE_REJECTED
        );
        // react-hooks/exhaustive-deps
    }, [props]);

    return (
        <>
            <table className={'custom-table'}>
                <thead>
                    <tr>
                        <Table.HeaderCell width={180}>Name</Table.HeaderCell>
                        <Table.HeaderCell width={280}>
                            Email Address
                        </Table.HeaderCell>
                        <Table.HeaderCell width={180}>
                            Timestamp
                        </Table.HeaderCell>
                        <Table.HeaderCell width={148}>Status</Table.HeaderCell>
                        <Table.HeaderCell width={0}>Comment</Table.HeaderCell>
                        <Table.HeaderCell width={24}>Expand</Table.HeaderCell>
                    </tr>
                </thead>
                <tbody>
                    <Fragment>
                        <StyledTableRowWithComment isExpand={isExpand}>
                            <StyledTd>{comment.fullName}</StyledTd>
                            <StyledTd>{comment.email}</StyledTd>
                            <StyledTd>
                                {/* TODO: Hot fix for project missing timestamp, should move to claim state */}
                                {formatToDateWithTime(time)}
                            </StyledTd>
                            <StyledTd>
                                {generateApproverStatusBadge(status)}
                            </StyledTd>
                            {isExpand ? (
                                <StyledTd>
                                    Comment: {ref}
                                </StyledTd>
                            ) : (
                                <StyledTd></StyledTd>
                            )}
                            <StyledTd>
                                {isEnableRejectArrow && (
                                    <>
                                        {isExpand ? (
                                                <Icon
                                                    onClick={handleToggleArrow}
                                                    onKeyUp={(event:KeyboardEvent) =>
                                                        {(event.keyCode === 13 || event.keyCode === 32) && handleToggleArrow()}
                                                    }
                                                    titleAccess={'ChevronUp'}
                                                    name={'ChevronUp'}
                                                    tabIndex={0}
                                                />
                                        ) : (
                                            <Icon
                                                onClick={handleToggleArrow}
                                                onKeyUp={(event:KeyboardEvent) =>
                                                    {(event.keyCode === 13 || event.keyCode === 32) && handleToggleArrow()}
                                                }
                                                titleAccess={'ChevronDown'}
                                                name={'ChevronDown'}
                                                tabIndex={0}
                                            />
                                        )}
                                    </>
                                )}
                            </StyledTd>
                        </StyledTableRowWithComment>
                    </Fragment>
                </tbody>
            </table>
        </>
    );
};

const StyledTd = styled.td`
    vertical-align: text-top;
`;

const StyledTableRow = styled(Table.BodyRow)`
    cursor: initial;
    :hover {
        background-color: #f9f9f9;
    }
`;

const StyledTdComment = styled(StyledTd)`
    position: absolute;
    left: 0;
    right: 0;
    top: 40px;
    background-color: #fff;
`;

const StyledTableRowWithComment = styled(StyledTableRow)<{ isExpand: boolean }>`
    position: relative;
    height: ${(props) => (props.isExpand ? "100px !important" : "")};
    td {
        vertical-align: ${(props) => (props.isExpand ? "text-top !important" : "")};
    }
    // max-height: 300px;
`;
